(() => {
  if (customElements.get('cahve-collection-page')) return;

  class CahveCollectionPage extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cahveCollectionReady === 'true') return;
      this.dataset.cahveCollectionReady = 'true';

      this.sectionId = this.dataset.sectionId;
      this.drawer = this.querySelector('[data-cahve-filter-drawer]');
      this.openButton = this.querySelector('[data-cahve-filter-open]');
      this.abortController = null;
      this.lastFocusedElement = null;

      this.onRootClick = this.onRootClick.bind(this);
      this.onDrawerClick = this.onDrawerClick.bind(this);
      this.onDrawerInput = this.onDrawerInput.bind(this);
      this.onDrawerSubmit = this.onDrawerSubmit.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);

      this.addEventListener('click', this.onRootClick);

      if (this.drawer) {
        this.drawer.addEventListener('click', this.onDrawerClick);
        this.drawer.addEventListener('input', this.onDrawerInput);
        this.drawer.addEventListener('submit', this.onDrawerSubmit);
        document.body.appendChild(this.drawer);
      }

      document.addEventListener('keydown', this.onDocumentKeydown);
      window.addEventListener('popstate', this.onPopState);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.removeEventListener('click', this.onRootClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      window.removeEventListener('popstate', this.onPopState);

      if (this.drawer) {
        this.drawer.removeEventListener('click', this.onDrawerClick);
        this.drawer.removeEventListener('input', this.onDrawerInput);
        this.drawer.removeEventListener('submit', this.onDrawerSubmit);
        this.drawer.remove();
      }

      document.body.classList.remove('cahve-filter-open');
    }

    onRootClick(event) {
      if (event.target.closest('[data-cahve-filter-open]')) this.openDrawer();
    }

    onDrawerClick(event) {
      if (event.target.closest('[data-cahve-filter-close]')) {
        this.closeDrawer();
        return;
      }

      if (event.target.closest('[data-cahve-filter-clear]')) {
        const searchParams = new URLSearchParams();
        const sortValue = this.drawer?.querySelector('[data-cahve-sort-hidden]')?.value;
        if (sortValue) searchParams.set('sort_by', sortValue);
        this.updateCollection(searchParams, { closeDrawer: true });
        return;
      }

      const sortChip = event.target.closest('[data-cahve-sort-chip]');
      if (sortChip) {
        const sortValue = sortChip.dataset.value;
        sortChip.parentElement
          ?.querySelectorAll('[data-cahve-sort-chip]')
          .forEach((chip) => chip.classList.toggle('is-active', chip === sortChip));

        const hiddenSortInput = this.drawer?.querySelector('[data-cahve-sort-hidden]');
        if (hiddenSortInput) hiddenSortInput.value = sortValue;

        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('sort_by', sortValue);
        searchParams.delete('page');
        this.updateCollection(searchParams);
      }
    }

    onDrawerInput(event) {
      const priceRangeInput = event.target.closest('[data-cahve-price-range]');
      if (priceRangeInput) {
        this.syncPriceSliderFromRange(priceRangeInput);
        return;
      }

      const priceTextInput = event.target.closest('[data-cahve-price-text]');
      if (priceTextInput) this.syncPriceSliderFromText(priceTextInput);
    }

    syncPriceSliderFromRange(rangeInput) {
      const slider = rangeInput.closest('[data-cahve-price-slider]');
      if (!slider) return;

      const minInput = slider.querySelector('[data-cahve-price-range="min"]');
      const maxInput = slider.querySelector('[data-cahve-price-range="max"]');
      if (!minInput || !maxInput) return;

      if (rangeInput === minInput && Number(minInput.value) > Number(maxInput.value)) {
        minInput.value = maxInput.value;
      } else if (rangeInput === maxInput && Number(maxInput.value) < Number(minInput.value)) {
        maxInput.value = minInput.value;
      }

      this.updatePriceSliderVisual(slider, minInput, maxInput);

      const priceWrap = slider.parentElement;
      const minText = priceWrap?.querySelector('[data-cahve-price-text="min"]');
      const maxText = priceWrap?.querySelector('[data-cahve-price-text="max"]');
      if (rangeInput === minInput && minText) minText.value = minInput.value;
      if (rangeInput === maxInput && maxText) maxText.value = maxInput.value;
    }

    syncPriceSliderFromText(textInput) {
      const priceWrap = textInput.closest('.cahve-filter-price');
      const slider = priceWrap?.previousElementSibling;
      if (!slider || !slider.matches('[data-cahve-price-slider]')) return;

      const minInput = slider.querySelector('[data-cahve-price-range="min"]');
      const maxInput = slider.querySelector('[data-cahve-price-range="max"]');
      if (!minInput || !maxInput) return;

      const numericValue = Number(String(textInput.value).replace(/[^0-9.-]/g, ''));
      if (Number.isNaN(numericValue)) return;

      const bounds = { min: Number(minInput.min), max: Number(minInput.max) };
      const clampedValue = Math.min(Math.max(numericValue, bounds.min), bounds.max);

      if (textInput.dataset.cahvePriceText === 'min') {
        minInput.value = Math.min(clampedValue, Number(maxInput.value));
      } else {
        maxInput.value = Math.max(clampedValue, Number(minInput.value));
      }

      this.updatePriceSliderVisual(slider, minInput, maxInput);
    }

    updatePriceSliderVisual(slider, minInput, maxInput) {
      const min = Number(minInput.min);
      const max = Number(minInput.max);
      const span = max - min || 1;
      const minPercent = ((Number(minInput.value) - min) / span) * 100;
      const maxPercent = ((Number(maxInput.value) - min) / span) * 100;
      slider.style.setProperty('--cahve-price-min-percent', `${minPercent}%`);
      slider.style.setProperty('--cahve-price-max-percent', `${maxPercent}%`);
    }

    onDrawerSubmit(event) {
      const form = event.target.closest('[data-cahve-filter-form]');
      if (!form) return;

      event.preventDefault();
      const searchParams = new URLSearchParams();
      new FormData(form).forEach((value, key) => {
        const normalizedValue = String(value).trim();
        if (normalizedValue) searchParams.append(key, normalizedValue);
      });
      searchParams.delete('page');
      this.updateCollection(searchParams, { closeDrawer: true });
    }

    onDocumentKeydown(event) {
      if (!this.drawer?.classList.contains('is-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(
        this.drawer.querySelectorAll(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onPopState() {
      this.updateCollection(new URLSearchParams(window.location.search), { updateHistory: false });
    }

    openDrawer() {
      if (!this.drawer) return;
      this.lastFocusedElement = document.activeElement;
      this.drawer.classList.add('is-open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.openButton?.setAttribute('aria-expanded', 'true');
      document.body.classList.add('cahve-filter-open');
      this.drawer.querySelector('[data-cahve-filter-panel]')?.focus({ preventScroll: true });
    }

    closeDrawer() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.openButton?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('cahve-filter-open');
      this.lastFocusedElement?.focus?.({ preventScroll: true });
    }

    setLoading(isLoading) {
      this.drawer?.classList.toggle('is-loading', isLoading);
      this.querySelector('#ProductGridContainer')?.classList.toggle('is-loading', isLoading);

      const status = this.drawer?.querySelector('[data-cahve-filter-status]');
      if (status && isLoading) status.textContent = 'Updating products';
    }

    async updateCollection(searchParams, options = {}) {
      const { closeDrawer = false, updateHistory = true } = options;
      this.abortController?.abort();
      const requestController = new AbortController();
      this.abortController = requestController;
      this.setLoading(true);

      const pageUrl = new URL(window.location.href);
      pageUrl.search = searchParams.toString();
      const sectionUrl = new URL(pageUrl);
      sectionUrl.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(sectionUrl.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          signal: requestController.signal,
        });
        if (!response.ok) throw new Error(`Collection update failed (${response.status})`);

        const html = await response.text();
        const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
        const sourceGrid = parsedDocument.querySelector('#ProductGridContainer');
        const sourceCount = parsedDocument.querySelector(`#CahveCollectionProductCount-${CSS.escape(this.sectionId)}`);
        const sourceFilterForm = parsedDocument.querySelector(`#CahveFilterForm-${CSS.escape(this.sectionId)}`);
        const sourceFilterTrigger = parsedDocument.querySelector('[data-cahve-filter-open]');
        const currentGrid = this.querySelector('#ProductGridContainer');
        const currentCount = this.querySelector(`#CahveCollectionProductCount-${CSS.escape(this.sectionId)}`);
        const currentFilterForm = this.drawer?.querySelector('[data-cahve-filter-form]');

        if (!sourceGrid || !currentGrid || !sourceCount || !currentCount) {
          throw new Error('The updated collection markup is incomplete.');
        }

        currentGrid.innerHTML = sourceGrid.innerHTML;
        currentCount.innerHTML = sourceCount.innerHTML;

        if (sourceFilterForm && currentFilterForm) currentFilterForm.innerHTML = sourceFilterForm.innerHTML;
        if (sourceFilterTrigger && this.openButton) this.openButton.innerHTML = sourceFilterTrigger.innerHTML;

        currentGrid.querySelectorAll('[data-cahve-fc-card]').forEach((card) => {
          card.classList.add('is-cahve-visible');
        });

        if (updateHistory) {
          history.pushState({ searchParams: searchParams.toString() }, '', `${pageUrl.pathname}${pageUrl.search}`);
        }

        document.dispatchEvent(
          new CustomEvent('cahve:collection:updated', {
            detail: { sectionId: this.sectionId, searchParams: searchParams.toString() },
          })
        );

        const status = this.drawer?.querySelector('[data-cahve-filter-status]');
        if (status) status.textContent = '';

        if (closeDrawer) this.closeDrawer();
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error(error);
        const status = this.drawer?.querySelector('[data-cahve-filter-status]');
        if (status) status.textContent = 'Products could not be updated. Please try again.';
      } finally {
        if (this.abortController === requestController) this.setLoading(false);
      }
    }
  }

  customElements.define('cahve-collection-page', CahveCollectionPage);
})();
